import gql from 'graphql-tag';
import { provideApolloClient } from '@vue/apollo-composable';
import { reactive, ref } from 'vue';
import useDecryptSymbols from './useDecryptSymbols';
import apolloClient from './useApolloClient';
import useAlerts from './useAlerts';
const { addAlert } = useAlerts();
const { decryptSymbols } = useDecryptSymbols();

const reportData = ref([]);
const rawReportData = ref([]);
const filterOptions = ref([]);
const loading = ref(false);

const columnsList = [
  { name: 'CREATED', title: 'Created', flexWidth: 3, order: 1 },
  { name: 'NAME', title: 'Name', flexWidth: 4, order: 2 },
  { name: 'SOURCE', title: 'Source', flexWidth: 3, order: 3 },
  { name: 'CHANNEL', title: 'Channel', flexWidth: 8, order: 4 },
  { name: 'LOCATION', title: 'City, State, Zip', flexWidth: 5, order: 5 },
  { name: 'INTERACTIONS', title: 'Interactions', flexWidth: 3, order: 6 },
  { name: 'STATUS', title: 'Status', flexWidth: 3, order: 7 },
  { name: 'ORDERVALUE', title: 'Order Value', flexWidth: 3, order: 8 },
  { name: 'COMPLETED', title: 'Completed', flexWidth: 3, order: 9 },
];

const filters = reactive({
  columns: [...columnsList.map((column) => column.name)],
  source: [],
  channel: [],
  channelDrilldown1: [],
  channelDrilldown2: [],
  startDate: '',
  endDate: '',
  status: 'all',
});

const defaultFilters = JSON.parse(JSON.stringify(filters));

const resetFilters = () => {
  for (const key in filters) {
    filters[key] = defaultFilters[key];
  }
};

const GET_IMPACT_REPORT = gql`
  query ImpactReport($filters: ImpactReportFilters) {
    impactReport(filters: $filters) {
      NAME
      COMPANYID
      CONTACTID
      LEADID
      SOURCE
      CHANNEL
      COMPLETE
      CREATEDATE
      COMPLETEDATE
      AMOUNT
      TOTALINTERACTIONS
      company_NAME
      contact_FIRSTNAME
      contact_LASTNAME
      CITY
      STATE
      POSTALCODE
    }
  }
`;

const IMPACT_REPORT_FILTERS_QUERY = gql`
  query ImpactReportFilters {
    impactReportFilters {
      NAME
      FILTERTYPE
    }
  }
`;

export const useSalesImpactReportQuery = function () {
  provideApolloClient(apolloClient);

  const runReport = async () => {
    loading.value = true;
    try {
      const filterParams = {
        dateFrom: filters.startDate,
        dateTo: filters.endDate,
        source: filters.source,
        channel: [...filters.channel],
        channelDrillDown1: [...filters.channelDrilldown1],
        channelDrillDown2: [...filters.channelDrilldown2],
        status: filters.status,
      };

      const { data } = await apolloClient.query({
        query: GET_IMPACT_REPORT,
        variables: { filters: filterParams },
        fetchPolicy: 'network-only',
      });

      if (data?.impactReport) {
        const decrypted = decryptSymbols(data.impactReport);
        rawReportData.value = decrypted;
        formatReportData(decrypted);
      }
    } catch (err) {
      console.error('Error fetching impact report:', err);
      addAlert('Error fetching impact report', 'high', 4);
    } finally {
      loading.value = false;
    }
  };

  return {
    reportData,
    loading,
    filters,
    runReport,
    columnsList,
    resetFilters,
    rawReportData,
  };
};

const formatReportData = (data) => {
  const formattedData = [];

  const formatDate = (date) => {
    try {
      return new Date(parseInt(date)).toLocaleDateString('en-US', {
        month: '2-digit',
        day: '2-digit',
        year: 'numeric',
      });
    } catch (error) {
      return '-';
    }
  };

  const parseChannel = (channel) => {
    try {
      const channelObject = JSON.parse(channel);
      return `Channel: ${channelObject.channel || '-'}, Drilldown1: ${channelObject.channelDrillDown1 || '-'}, Drilldown2: ${channelObject.channelDrillDown2 || '-'}`;
    } catch (error) {
      return '-';
    }
  };

  const getAddress = (lead) => {
    const city = lead.CITY || '';
    const state = lead.STATE || '';
    const postalCode = lead.POSTALCODE || '';

    return `${city}${city && (state || postalCode) ? ', ' : ''}${state}${state && postalCode ? ' ' : ''}${postalCode}`;
  };

  const getLeadName = (lead) => {
    return lead.COMPANYID ? lead.company_NAME : lead.CONTACTID ? `${lead.contact_FIRSTNAME} ${lead.contact_LASTNAME}` : lead.NAME;
  };

  data.forEach((lead) => {
    formattedData.push({
      CREATED: formatDate(lead.CREATEDATE),
      NAME: getLeadName(lead),
      SOURCE: lead.SOURCE,
      CHANNEL: parseChannel(lead.CHANNEL),
      LOCATION: getAddress(lead),
      INTERACTIONS: lead.TOTALINTERACTIONS,
      STATUS: lead.COMPLETE ? 'Closed' : 'Open',
      ORDERVALUE: lead.AMOUNT && lead.COMPLETE ? `$${lead.AMOUNT}` : '-',
      COMPLETED: lead.COMPLETE ? formatDate(lead.COMPLETEDATE) : '-',
      COMPANYID: lead.COMPANYID,
      CONTACTID: lead.CONTACTID,
      LEADID: lead.LEADID,
      CITY: lead.CITY,
      STATE: lead.STATE,
      POSTALCODE: lead.POSTALCODE,
    });
  });

  reportData.value = formattedData;
  return formattedData;
};

export const useSalesImpactReportFilters = () => {
  provideApolloClient(apolloClient);

  const runGetFilters = async () => {
    try {
      const { data } = await apolloClient.query({
        query: IMPACT_REPORT_FILTERS_QUERY,
        fetchPolicy: 'network-only',
      });

      filterOptions.value = data?.impactReportFilters || [];
    } catch (err) {
      console.error('Error fetching impact report filters:', err);
      addAlert('Error fetching impact report filters', 'high', 4);
    }
  };

  return {
    filterOptions,
    runGetFilters,
  };
};
